import { ReportUrl, ReportTabType } from "@common";
import { DefaultLayout } from "@layouts";
import { AppState } from "@store";
import { History, Location } from "history";
import { FetchingState, ReduxHttpRequestState } from "rd-redux-http";
import * as React from "react";
import { connect } from "react-redux";
import "./ReportLayout.css";
import { ReportNavigation } from "./ReportNavigation";
import { ReportsAside } from "./ReportsAsideComponent";
import { RocketComponent } from "@components";

interface ReportLayoutComponentProps {
    loading: boolean;
    loadingFailed: boolean;
    subtitle?: string;
    children?: React.ReactChild | React.ReactChild[];
    history: History;
    location: Location;
    clientId: string;
    hasData: boolean;
}

function ReportLayoutComponent(props: ReportLayoutComponentProps) {
    return (
        <DefaultLayout
            selectedPage="reports"
            asideChildren={() => <ReportsAside />}
            loading={props.hasData && props.loading}
            loadingFailed={props.loadingFailed}
            clientId={props.clientId}
            history={props.history}
            location={props.location}
            disableContent={false}
        >
            <section className="report-header">
                <h2 className="report-header__title">
                    <span className="report-header__main-title">Report</span>
                    <span className="report-header__subtitle">{props.subtitle}</span>
                </h2>
                <ReportNavigation className="report-header__navigation" />
            </section>
            {props.hasData ? (
                <section className="report-content">{props.children}</section>
            ) : (
                <section className="report-content">
                    <RocketComponent />
                </section>
            )}
        </DefaultLayout>
    );
}

export interface ReportLayoutProps {
    children?: React.ReactChild | React.ReactChild[];
    subtitle?: string;
    history: History;
    location: Location;
}

type ReportTabLoadable = { [P in ReportTabType]?: ReduxHttpRequestState<any, any, any>[] };

export const ReportLayout = connect(
    (state: AppState, ownProps: ReportLayoutProps): ReportLayoutComponentProps => {
        const matchReportPage = ReportUrl.match(ownProps.location, true);

        const chartState = state.reports.chartData;
        const mappingsState = state.mappings;

        const watchForLoading = [state.campaignList.campaigns, state.pagesState.clients];

        const tabWatchForLoading: ReportTabLoadable = {
            dynamic: [chartState.overallData, chartState.dateData, chartState.environmentData, chartState.weekdayData],
            audience: [
                chartState.ageGroupData,
                chartState.genderData,
                chartState.osData,
                mappingsState.age,
                mappingsState.gender,
                mappingsState.devices
            ],
            geographic: [chartState.overallData, chartState.regionData, state.reports.countries],
            creative: [
                state.mappings.adunits,
                chartState.adFormatData,
                chartState.adFormatAdTemplateData,
                chartState.adInteractionData
            ]
        };

        const clientId = matchReportPage && matchReportPage.isMatched && matchReportPage.params.clientId;

        let isExistingClient: boolean = true;

        if (clientId !== "direct" && FetchingState.isSuccess(state.pagesState.clients)) {
            isExistingClient = state.pagesState.clients.data.clients.find(c => c.clientId === clientId) ? true : false;
        }

        return {
            hasData:
                FetchingState.isInitialOrFetching(state.campaignList.campaigns) ||
                FetchingState.getDataOrDefault(state.campaignList.campaigns, []).filter(
                    c => c.status !== "draft" && c.status !== "pending"
                ).length > 0,
            clientId,
            ...ownProps,
            loading:
                !matchReportPage.isMatched ||
                watchForLoading.some(s => FetchingState.isInitialOrFetching(s as any)) ||
                (tabWatchForLoading[matchReportPage.params.tab] &&
                    tabWatchForLoading[matchReportPage.params.tab].some(i => FetchingState.isInitialOrFetching(i))) ||
                state.clientState.clientSavingFlag,
            loadingFailed:
                !matchReportPage.isMatched ||
                watchForLoading.some(s => FetchingState.isError(s as any)) ||
                (tabWatchForLoading[matchReportPage.params.tab] &&
                    tabWatchForLoading[matchReportPage.params.tab].some(i => FetchingState.isError(i))) ||
                !isExistingClient
        };
    }
)(ReportLayoutComponent);
